import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box, Card, CardContent, Chip, Stack, Tooltip, Typography,
} from '@mui/material';
import BoltIcon from '@mui/icons-material/Bolt';

import { fetchRequest, clearState } from '../../redux/actions/discovery/shockAnalogues';

const fmtWhen = (ms) => {
  if (!ms) return '—';
  try {
    return new Date(ms).toISOString().slice(0, 10);
  } catch (e) {
    return '—';
  }
};

/**
 * Shock analogue evidence fan (plan E9 / Step 13): "in k similar past
 * events, the target moved X% to Y% within 24h". Retrieved from the
 * embedding index when the shock entered; display-grade evidence only —
 * never applied to forecasts. Renders nothing when the goal has no shock
 * entry or Qdrant was down when it fired.
 */
const ShockAnaloguesCard = ({ goal }) => {
  const dispatch = useDispatch();
  const state = useSelector((s) => s.discovery_shock_analogues);
  const inShock = goal?.shock_state === 'shock' || goal?.shock_state === 'recovery';

  useEffect(() => {
    if (goal?.goal_id && inShock) {
      dispatch(fetchRequest({ goalId: goal.goal_id }));
    }
    return () => { dispatch(clearState()); };
  }, [dispatch, goal?.goal_id, goal?.shock_state, inShock]);

  const fan = state?.response?.analogues;
  if (!inShock || !fan || !fan.k) return null;

  const horizonH = Math.round((fan.horizon_s || 86400) / 3600);
  const hasRange = fan.move_min_pct != null && fan.move_max_pct != null;

  return (
    <Card variant="outlined">
      <CardContent>
        <Stack spacing={1}>
          <Stack direction="row" spacing={1} alignItems="center">
            <BoltIcon sx={{ fontSize: 18, color: 'warning.main' }} />
            <Typography variant="subtitle2">Similar past shocks</Typography>
            <Tooltip
              arrow
              title="Retrieved from the embedding index — similar event in a similar situation. Evidence only; forecasts are never adjusted from analogues."
            >
              <Chip size="small" variant="outlined" label={`evidence fan · v${fan.embedding_version}`} />
            </Tooltip>
          </Stack>

          {hasRange && (
            <Typography variant="body2">
              In {fan.k} similar past event{fan.k === 1 ? '' : 's'}, the target moved
              {' '}
              <Box component="span" sx={{ fontWeight: 700 }}>
                {fan.move_min_pct}% to {fan.move_max_pct}%
              </Box>
              {' '}within {horizonH}h.
            </Typography>
          )}

          <Stack spacing={0.5}>
            {(fan.analogues || []).map((a) => (
              <Stack key={a.event_id} direction="row" spacing={1.5} alignItems="baseline">
                <Typography variant="caption" sx={{ width: 84, flexShrink: 0, color: 'text.secondary' }}>
                  {fmtWhen(a.occurred_at)}
                </Typography>
                <Typography variant="caption" noWrap sx={{ flexGrow: 1, minWidth: 0 }} title={a.name || a.stream}>
                  {a.name || a.stream}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  sim {Math.round((a.similarity || 0) * 100)}%
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    width: 64, textAlign: 'right', fontWeight: 600,
                    color: a.move_pct == null ? 'text.disabled'
                      : a.move_pct < 0 ? 'error.main' : 'success.main',
                  }}
                >
                  {a.move_pct == null ? 'n/a' : `${a.move_pct > 0 ? '+' : ''}${a.move_pct}%`}
                </Typography>
              </Stack>
            ))}
          </Stack>

          <Typography variant="caption" color="text.disabled">
            Display-grade evidence from the workspace&apos;s event history — never auto-applied to forecasts.
          </Typography>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default ShockAnaloguesCard;
